/**
 * 语言工具函数
 * 统一管理所有语言相关的工具函数和配置
 */

/**
 * 按中文名称拼音排序语言选项（用于下拉框等展示顺序）
 * 使用 localeCompare('zh-CN') 保证中文按拼音 A-Z 显示
 */
export function sortLanguageOptionsByLabel<T extends { label: string }>(options: T[]): T[] {
  if (!Array.isArray(options) || options.length === 0) return options
  return [...options].sort((a, b) =>
    (a.label || '').localeCompare(b.label || '', 'zh-CN', { sensitivity: 'accent' })
  )
}

/**
 * 按 chinese_name 拼音排序带中文名的对象数组（用于任务弹窗等语言列表）
 */
export function sortByChineseName<T extends { chinese_name: string }>(items: T[]): T[] {
  if (!Array.isArray(items) || items.length === 0) return items
  return [...items].sort((a, b) =>
    (a.chinese_name || '').localeCompare(b.chinese_name || '', 'zh-CN', { sensitivity: 'accent' })
  )
}

/**
 * 语言信息接口
 */
export interface LanguageInfo {
  /** 语言代码 */
  code: string
  /** 中文名称 */
  name: string
  /** 本地名称 */
  nativeName: string
  /** 是否启用 */
  enabled?: boolean
}

/**
 * 语言代码映射表
 */
export const LANGUAGE_MAP: Record<string, LanguageInfo> = {
  zh: {
    code: 'zh',
    name: '中文',
    nativeName: '中文'
  },
  en: {
    code: 'en',
    name: '英语',
    nativeName: 'English'
  },
  ja: {
    code: 'ja',
    name: '日语',
    nativeName: '日本語'
  },
  ko: {
    code: 'ko',
    name: '韩语',
    nativeName: '한국어'
  },
  fr: {
    code: 'fr',
    name: '法语',
    nativeName: 'Français'
  },
  de: {
    code: 'de',
    name: '德语',
    nativeName: 'Deutsch'
  },
  es: {
    code: 'es',
    name: '西班牙语',
    nativeName: 'Español'
  },
  it: {
    code: 'it',
    name: '意大利语',
    nativeName: 'Italiano'
  },
  ru: {
    code: 'ru',
    name: '俄语',
    nativeName: 'Русский'
  },
  pt: {
    code: 'pt',
    name: '葡萄牙语',
    nativeName: 'Português'
  },
  ar: {
    code: 'ar',
    name: '阿拉伯语',
    nativeName: 'العربية'
  },
  th: {
    code: 'th',
    name: '泰语',
    nativeName: 'ไทย'
  },
  vi: {
    code: 'vi',
    name: '越南语',
    nativeName: 'Tiếng Việt'
  },
  id: {
    code: 'id',
    name: '印尼语',
    nativeName: 'Bahasa Indonesia'
  },
  hi: {
    code: 'hi',
    name: '印地语',
    nativeName: 'हिन्दी'
  }
}

/**
 * 获取语言名称
 * @param code 语言代码
 * @param locale 语言环境，'zh' 返回中文名称，'native' 返回本地名称，默认为 'zh'
 * @returns 语言名称
 */
export function getLanguageName(code: string, locale: 'zh' | 'native' = 'zh'): string {
  const lang = LANGUAGE_MAP[code]
  if (!lang) return code
  
  return locale === 'native' ? lang.nativeName : lang.name
}

/**
 * 获取语言标签（显示格式：中文名称 (本地名称)）
 * @param code 语言代码
 * @returns 语言标签
 */
export function getLanguageTag(code: string): string {
  const lang = LANGUAGE_MAP[code]
  if (!lang) return code
  
  if (lang.name === lang.nativeName) {
    return lang.name
  }
  
  return `${lang.name} (${lang.nativeName})`
}

/**
 * 格式化语言列表
 * @param codes 语言代码数组
 * @param separator 分隔符，默认为 ', '
 * @param locale 语言环境
 * @returns 格式化后的语言列表字符串
 */
export function formatLanguageList(
  codes: string[],
  separator = ', ',
  locale: 'zh' | 'native' = 'zh'
): string {
  if (!codes || codes.length === 0) return '-'
  
  return codes.map(code => getLanguageName(code, locale)).join(separator)
}

/**
 * 获取所有语言列表
 * @param enabledOnly 是否只返回启用的语言，默认为 false
 * @returns 语言信息数组
 */
export function getAllLanguages(enabledOnly = false): LanguageInfo[] {
  const languages = Object.values(LANGUAGE_MAP)
  
  if (enabledOnly) {
    return languages.filter(lang => lang.enabled !== false)
  }
  
  return languages
}

/**
 * 检查语言代码是否有效
 * @param code 语言代码
 * @returns 是否有效
 */
export function isValidLanguageCode(code: string): boolean {
  return code in LANGUAGE_MAP
}

/**
 * 获取语言选项（用于下拉选择）
 * @param enabledOnly 是否只返回启用的语言
 * @returns 语言选项数组
 */
export function getLanguageOptions(enabledOnly = false): Array<{ label: string; value: string }> {
  const languages = getAllLanguages(enabledOnly)
  
  return languages.map(lang => ({
    label: getLanguageTag(lang.code),
    value: lang.code
  }))
}

/**
 * 根据语言代码获取语言信息
 * @param code 语言代码
 * @returns 语言信息，如果不存在则返回 null
 */
export function getLanguageInfo(code: string): LanguageInfo | null {
  return LANGUAGE_MAP[code] || null
}

